import { ref, watch, computed } from 'vue';
import type { Ref } from 'vue';
import type { FilterConfiguration } from '@/components/base/dynamicFilterBuilder';
import { useI18n } from '@/composables/useI18n';
import { useAbortController } from "@/composables/useAbortController";
import type { PointOfSale, FetchPointOfSalesParam, Customer } from '@/components/customers/scripts';
import { fetchCustomerPointOfSales } from '@/components/customers/scripts/pointOfSaleApiRequests';
import { isCustomer } from '@/components/customers/scripts/typeGuards';

/**
 * Options for configuring the point of sale filter.
 */
interface PointOfSaleFilterOptions {
  /**
   * Callback function to be called when an error occurs.
   * @param error - The error object.
   */
  onError?: (error: unknown) => void;

  /**
   * Reactive reference to the currently selected customer. When this changes,
   * the point of sale filter will be updated and cleared if no customer is selected.
   * Can be Customer, null, or string (if user types in combobox without selecting).
   */
  selectedCustomer: Ref<Customer | null | string>;
}

/**
 * Composable for creating and managing a point of sale filter.
 * The filter is dependent on the selected customer and will be disabled when no customer is selected.
 * @param options - Configuration options for the point of sale filter.
 * @returns An object containing the point of sale filter configuration and the selected point of sale.
 */
export const usePointOfSaleFilterWithCustomerDependency = (options: PointOfSaleFilterOptions) => {
  const { t } = useI18n();
  const { getNewAbortController } = useAbortController();
  const selectedPointOfSale = ref<PointOfSale[] | null>(null);
  const refreshTrigger = ref(0);

  const isDisabled = computed(() => !isCustomer(options.selectedCustomer.value));

  const getFilteredItems = async (searchInput: string | null) => {
    if (!isCustomer(options.selectedCustomer.value)) {
      return [];
    }

    const fetchArgs: FetchPointOfSalesParam = {
      customerId: (options.selectedCustomer.value as Customer).id,
      pageSize: 1000, // Get all point of sales for the customer,
      sortColumn: "name",
      sortDirection: "asc"
    };
    if (searchInput) fetchArgs.name = searchInput;

    const { error, response } = await fetchCustomerPointOfSales(fetchArgs, getNewAbortController().signal);

    if (error) {
      options?.onError?.(error);
      return [];
    }

    return response || [];
  }
  /**
   * Creates a filter configuration for selecting point of sales.
   * @param config - Configuration for the filter including column properties, events, and model.
   * @returns The filter configuration object.
   */
  const createPointOfSaleFilterConfiguration = (
    config?: Pick<FilterConfiguration, 'colProps' | 'events' | 'debounce' | 'inAdvancedSection'>
  ): FilterConfiguration => {
    const filterConfig: FilterConfiguration = {
      ...(config ?? {}),
      type: "combobox",
      props: computed(() => ({
        label: t("MerchantTransactionManagement.point_of_sale_selection_field"),
        "item-text": "name",
        placeholder: t("MerchantTransactionManagement.point_of_sale_selection_field"),
        disabled: isDisabled.value,
        multiple: true,
      })),
      model: selectedPointOfSale,
      getFilteredItems: getFilteredItems,
      refreshTrigger: refreshTrigger
    };

    return filterConfig;
  };

  const resolveSelectedPointOfSales = async (ids: number[]) => {
    if (!isCustomer(options.selectedCustomer.value) || ids.length === 0) {
      selectedPointOfSale.value = null;
      return;
    }

    const { error, response } = await fetchCustomerPointOfSales({
      customerId: options.selectedCustomer.value.id,
      pageSize: 1000,
    }, getNewAbortController().signal);

    if (error) {
      options.onError?.(error);
      return;
    }

    selectedPointOfSale.value = (response ?? []).filter(pointOfSale => ids.includes(pointOfSale.id));
  };

  // Watch for customer changes and clear point of sale selection when customer changes
  watch(options.selectedCustomer, (newCustomer) => {
    selectedPointOfSale.value = null;
    if (isCustomer(newCustomer)) {
      // Refresh point of sale items when customer changes whichs calls getFilteredItems(null) to load all point of sales for the new customer
      refreshTrigger.value++;
    }
  });

  return {
    createPointOfSaleFilterConfiguration,
    selectedPointOfSale,
    resolveSelectedPointOfSales,
  };
};
